import { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Save, Mail, Key, AlertCircle, Database, Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const SettingsPage = () => {
  const [postmarkApiKey, setPostmarkApiKey] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [showApiKey, setShowApiKey] = useState(false);
  const [restoreUrl, setRestoreUrl] = useState('');
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoadingSettings(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.postmark_api_key) {
        setPostmarkApiKey(response.data.postmark_api_key);
      }
      if (response.data.sender_email) {
        setSenderEmail(response.data.sender_email);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Don't show error toast on initial load if settings don't exist
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleSaveSettings = async () => {
    // Validation
    if (!postmarkApiKey.trim()) {
      toast.error('Postmark API Key is required');
      return;
    }

    if (!senderEmail.trim()) {
      toast.error('Sender Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(senderEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/settings`,
        {
          postmark_api_key: postmarkApiKey.trim(),
          sender_email: senderEmail.trim()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    if (!postmarkApiKey.trim() || !senderEmail.trim()) {
      toast.error('Please save your settings first');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/admin/settings/test-email`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Test email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  const handleBackupDatabase = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/backup-database`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const { status, message, bucket } = response.data;
      
      if (status === 'processing') {
        toast.success(
          <div>
            <p className="font-semibold">Backup started!</p>
            <p className="text-sm mt-1">{message}</p>
            <p className="text-sm mt-2">Wait 1-2 minutes, then click "Get Latest Backup" to download.</p>
          </div>,
          { duration: 8000 }
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start database backup');
    } finally {
      setLoading(false);
    }
  };

  const handleGetLatestBackup = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/backup-database/latest`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = response.data;
      
      if (data.status === 'available') {
        // Open download URL in new tab
        window.open(data.download_url, '_blank');
        
        toast.success(
          <div>
            <p className="font-semibold">Backup ready!</p>
            <p className="text-sm mt-1">Filename: {data.filename}</p>
            <p className="text-sm">Size: {data.size_kb} KB</p>
            <p className="text-sm">Created: {new Date(data.created_at).toLocaleString()}</p>
          </div>,
          { duration: 6000 }
        );
      } else if (data.status === 'no_backups') {
        toast.info(data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get latest backup');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreDatabase = async () => {
    if (!restoreUrl.trim()) {
      toast.error('Please enter a backup URL');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('backup_url', restoreUrl.trim());

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/restore-database`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000 // 2 minutes
        }
      );

      const data = response.data;
      
      if (data.status === 'success') {
        toast.success(
          <div>
            <p className="font-semibold">Database restored successfully!</p>
            <p className="text-sm mt-1">Total documents: {data.total_documents}</p>
            <p className="text-sm">Collections: {Object.keys(data.collections_imported).length}</p>
            <p className="text-sm mt-2">Please refresh the page.</p>
          </div>,
          { duration: 10000 }
        );
        
        setRestoreUrl('');
        setShowRestoreConfirm(false);
        
        // Reload page after 3 seconds
        setTimeout(() => window.location.reload(), 3000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore database');
    } finally {
      setLoading(false);
    }
  };

  if (loadingSettings) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#EDEDF0' }}>
        <div className="text-center">
          <div className="inline-block w-12 h-12 rounded-full animate-spin mb-4" style={{ border: '3px solid rgba(191,33,47,0.2)', borderTopColor: '#BF212F' }}></div>
          <p className="font-tac-mono tac-kicker" style={{ fontSize: 11, color: '#9A9AA0' }}>Loading settings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-tac-body" style={{ background: '#EDEDF0' }}>
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div className="sticky top-0 z-10" style={{ background: '#fff', borderBottom: '1px solid #E4E4E9' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between" style={{ height: 64 }}>
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-[#BF212F]" />
              <h1 className="font-tac-display" style={{ fontWeight: 800, fontSize: 22, color: '#141414', letterSpacing: '-0.015em' }}>Settings</h1>
            </div>
            <button onClick={() => window.history.back()} className="font-tac-heading" style={{ border: '1px solid #E4E4E9', color: '#141414', borderRadius: 9, padding: '9px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer', background: '#fff' }}>Back to dashboard</button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Email Configuration Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Mail className="w-5 h-5 text-[#BF212F]" />
              <span>Email Configuration</span>
            </CardTitle>
            <CardDescription>
              Configure Postmark settings for sending gallery invitations and notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">How to get your Postmark API Key:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Log in to <a href="https://account.postmarkapp.com/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Postmark Account</a></li>
                  <li>Go to <strong>Servers</strong> → Select your server</li>
                  <li>Navigate to <strong>API Tokens</strong> tab</li>
                  <li>Copy your <strong>Server API Token</strong></li>
                </ol>
              </div>
            </div>

            {/* Postmark API Key */}
            <div className="space-y-2">
              <Label htmlFor="postmark-api-key" className="flex items-center space-x-2">
                <Key className="w-4 h-4 text-slate-600" />
                <span>Postmark API Key</span>
              </Label>
              <div className="relative">
                <Input
                  id="postmark-api-key"
                  type={showApiKey ? "text" : "password"}
                  value={postmarkApiKey}
                  onChange={(e) => setPostmarkApiKey(e.target.value)}
                  placeholder="Enter your Postmark Server API Token"
                  className="pr-24"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  {showApiKey ? 'Hide' : 'Show'}
                </Button>
              </div>
            </div>

            {/* Sender Email */}
            <div className="space-y-2">
              <Label htmlFor="sender-email" className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-slate-600" />
                <span>Sender Email Address</span>
              </Label>
              <Input
                id="sender-email"
                type="email"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder="hello@turbo360.com.au"
              />
              <p className="text-xs text-slate-500">
                This email must be verified in your Postmark account
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3 pt-4">
              <Button
                onClick={handleSaveSettings}
                disabled={loading}
                className="bg-[#BF212F] hover:bg-[#8B1923]"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Settings'}
              </Button>
              
              <Button
                onClick={handleTestEmail}
                disabled={loading}
                variant="outline"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Test Email
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Database Backup Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Database className="w-5 h-5 text-[#BF212F]" />
              <span>Database Backup</span>
            </CardTitle>
            <CardDescription>
              Export all database collections and data for backup or migration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">What's included in the backup:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>All galleries with metadata</li>
                  <li>All photos with tags and AI analysis</li>
                  <li>User accounts and settings</li>
                  <li>Download logs and analytics data</li>
                  <li>Complete schema and relationships</li>
                </ul>
                <p className="mt-2 font-medium">The backup file is in JSON format and can be imported into a new MongoDB database.</p>
              </div>
            </div>

            {/* Backup Buttons */}
            <div className="flex items-center space-x-3">
              <Button
                onClick={handleBackupDatabase}
                disabled={loading}
                className="bg-[#0A0A0B] hover:bg-[#2B2B2B]"
              >
                <Download className="w-4 h-4 mr-2" />
                {loading ? 'Creating Backup...' : 'Create Database Backup'}
              </Button>
              
              <Button
                onClick={handleGetLatestBackup}
                disabled={loading}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Get Latest Backup
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Database Restore Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Upload className="w-5 h-5 text-green-500" />
              <span>Restore Database</span>
            </CardTitle>
            <CardDescription>
              Import data from a backup JSON file URL (S3, public URL, etc.)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            
            {/* Warning Banner */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-medium mb-1">⚠️ Warning: This will replace ALL existing data!</p>
                <ul className="list-disc list-inside space-y-1 text-red-700">
                  <li>All current galleries, photos, and users will be deleted</li>
                  <li>Data will be replaced with the backup file contents</li>
                  <li>This action cannot be undone</li>
                  <li>Make sure to backup current data first!</li>
                </ul>
              </div>
            </div>

            {/* Backup URL Input */}
            <div className="space-y-2">
              <Label htmlFor="restore-url">Backup File URL</Label>
              <Input
                id="restore-url"
                type="url"
                value={restoreUrl}
                onChange={(e) => setRestoreUrl(e.target.value)}
                placeholder="https://s3.amazonaws.com/.../backup.json"
                disabled={loading}
              />
              <p className="text-xs text-slate-500">
                Paste the S3 URL or any public URL to your backup JSON file
              </p>
            </div>

            {/* Restore Buttons */}
            {!showRestoreConfirm ? (
              <Button
                onClick={() => setShowRestoreConfirm(true)}
                disabled={loading || !restoreUrl.trim()}
                variant="outline"
                className="border-red-600 text-red-600 hover:bg-red-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Restore Database
              </Button>
            ) : (
              <div className="flex items-center space-x-3">
                <Button
                  onClick={handleRestoreDatabase}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {loading ? 'Restoring...' : 'Confirm Restore'}
                </Button>
                <Button
                  onClick={() => setShowRestoreConfirm(false)}
                  disabled={loading}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Need Help?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <h4 className="font-medium text-slate-800 mb-1">Email not sending?</h4>
              <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
                <li>Verify your sender email is approved in Postmark</li>
                <li>Check your Postmark account is active</li>
                <li>Make sure you're using the Server API Token (not Account API Token)</li>
                <li>Check backend logs for specific error messages</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-slate-800 mb-1">Emails going to spam?</h4>
              <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
                <li>Configure SPF, DKIM, and DMARC records for your domain</li>
                <li>Warm up your sending domain gradually</li>
                <li>Use a verified domain (not @gmail.com, @outlook.com, etc.)</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
